import * as React from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';

import { Button as ButtonV2, Heading } from '@restworld/ui-ds-base';
import { cn, customImageLoader } from '@restworld/utils-common';
import { useEmployerUserContactData } from '@restworld/feature-authentication';

import { useEmployerUserInformationMutation } from './use-employer-user-information';

export const UploadProfilePicture = () => {
  const { t } = useTranslation('user-settings');
  const ref = React.useRef<HTMLInputElement>(null);
  const { picture_url, user_id } = useEmployerUserContactData();
  const { mutate, isPending } = useEmployerUserInformationMutation();

  const handleFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.[0]) {
        mutate({ picture: event.target.files[0], id: user_id });
      }
    },
    [mutate, user_id]
  );

  return (
    <UploadLabel htmlFor="upload-image">
      <ImageContainer>
        {picture_url ? (
          <Image
            fill
            loader={customImageLoader}
            alt="profile-pic"
            src={picture_url}
            placeholder="empty"
            className="rounded-lg object-cover object-center"
          />
        ) : null}
        <ButtonV2
          variant="tertiary"
          StartIcon={ArrowUpTrayIcon}
          onClick={() => ref.current?.click()}
          size="sm"
          loading={isPending}
          className={'bg-white'}
        >
          {t('upload_image')}
        </ButtonV2>
      </ImageContainer>
      <HiddenInput onChange={handleFileChange} type="file" id="picture" accept=".jpg,.png" ref={ref} />
      <Heading className="text-neutral-200 text-sm font-medium">
        {t('file_format_supported')}: .jpg, .png, etc. Max. file size: 30 MB
      </Heading>
    </UploadLabel>
  );
};

const UploadLabel = ({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label className={cn('flex-col justify-end items-start gap-4 inline-flex', className)} {...props} />
);
const ImageContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'rounded-lg relative bg-purple-50 flex-col justify-center items-center gap-2.5 flex h-50 w-50',
      className
    )}
    {...props}
  />
);
const HiddenInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => <input className={cn('hidden', className)} ref={ref} {...props} />
);
HiddenInput.displayName = 'HiddenInput';
